import { activeCardholderTerm } from "./terminology.js";
// Stage 19d — centralised display-label mapping for snake_case enums.
//
// Pre-19d: components rendered raw API values directly:
//   "active partner" / "support_query" / "first_edition" / "happy"
// The design audit's batch B was 5 issues all rooted in this. Single
// source of truth lives here; everywhere a display value renders, the
// component goes through `displayLabel(category, value)`.
//
// Logic values (filter state, equality checks, conditional branches)
// keep using the snake_case enum — that's the wire format. Only the
// rendered strings come from this file.
//
// Forward-compat: when a new enum value lands in the API without a
// matching map entry, `displayLabel` falls back to a Title Case'd
// snake_case → words form so the UI never leaks the raw token.

// Spec §4.3 (v5.2). Stage 33a expanded from 9 → 15 labels. `done`
// and `on_hold` are no longer accepted by the backend validator but
// stay here as forward-compat fallback for any cached payload still
// carrying them. `qa` is mapped explicitly to "QA" — the titleCase
// fallback would render "Qa", which is wrong.
// The statuses only an engineering ticket carries (contract section 21 b). They
// are kept apart so the support Status box, which lists every status of the
// plain map below, does not offer them: utils/ticketVocabulary.ts filters on this.
export const ENGINEERING_ONLY_TICKET_STATUS_LABELS = {
	ready_for_shaping: "Ready for Shaping",
	ready_for_agent: "Ready for Agent",
	agent_finished: "Agent Finished",
	engineer_pickup: "Ready to build",
	needs_attention: "Needs Attention",
	awaiting_business_action: "Awaiting Business Action",
	ready_for_testing: "Ready for Testing",
	awaiting_reporter_action: "Awaiting Reporter Action",
	fixed_by_other: "Fixed by Other",
	cancelled: "Cancelled",
} as const;

export const TicketStatus = {
	...ENGINEERING_ONLY_TICKET_STATUS_LABELS,
	new: "New",
	triaging: "Triaging",
	estimation: "Estimation",
	open: "Open",
	// Round 6 (contract section 12): two support statuses of their own, named to
	// Communico staff only. A library is never sent them; it is sent a partner status.
	pending_support_general: "Pending Support General",
	in_progress: "In Progress",
	code_review: "Code Review",
	qa: "QA",
	waiting_on_partner: "Waiting on Partner",
	// Marcie R5/R6 renames (backend enum values customer_replied/waiting_on_dev
	// preserved — display-only).
	customer_replied: "New Partner Reply",
	waiting_on_dev: "With Engineering",
	update_for_dev_pending: "UPDATE for Dev Pending",
	blocked: "Blocked",
	release_queue: "Release Queue",
	waiting_for_build: "Waiting for Build",
	resolved: "Resolved",
	closed: "Closed",
	// Forward-compat fallbacks (Stage 33a removed from enum):
	on_hold: "On Hold",
	done: "Done",
	archived: "Archived",
} as const;

// Marcie R4 — DISPLAY labels for every ticket type. The first six are the
// current picker (her A-Z set); the rest are RETIRED from the picker but kept
// here so a grandfathered row still renders its real label (Marcie: "the couple
// of old tickets keep their label"). "Support Query" was never a real Freshdesk
// type — the import defaulted every ticket to it; migration 000134 recovered
// the real types, so no live ticket should carry it, but display stays correct
// if one lingers.
export const TicketType = {
	bug: "Bug",
	chore: "Change/Task Request",
	enhancement: "Enhancement",
	feature: "Feature",
	help_wanted: "Help Wanted",
	question: "Question",
	// retired from the picker (still valid backend-side; display-only here)
	support_query: "Support Query",
	uat: "UAT",
	epic: "Epic",
	ob_story: "Onboarding",
	initiative: "Initiative",
	// Engineering only (contract section 21 a).
	investigation: "Investigation",
} as const;

// The six types offered in the picker (create + edit), A-Z. Deliberately a
// SUBSET of TicketType — the retired keys above render but are never selectable.
const PICKER_TICKET_TYPES = ['bug', 'chore', 'enhancement', 'feature', 'help_wanted', 'question'] as const;

// Marcie Round 2 (B10) — the SINGLE source of truth for the ticket-type
// picker. Both the create form (cos-ticket-create) and the edit-properties
// modal (cos-ticket-properties-modal) render from this, so the two option
// lists can never drift again. Derived from TicketType (the canonical labels).
export const TICKET_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> =
	PICKER_TICKET_TYPES.map((value) => ({ value, label: TicketType[value] }));

// Marcie R5/R6 — priority collapses to THREE meaningful levels: High / Medium /
// Low (she disliked "P1–P4" and the "2 Highs"). The enum VALUES stay p1–p4
// (backend preserved); p1→High, p2→Medium, p3→Low, and p4 (the Freshdesk import
// default, ~40k mostly-closed rows) FOLDS into Low. The picker offers only the
// three (p1/p2/p3); a p4 row displays "Low". Colours: High=red, Medium=amber,
// Low=green (see badge-tokens / priorityColor).
export const TicketPriority = {
	p1: "High",
	p2: "Medium",
	p3: "Low",
	p4: "Low",
} as const;

// Compact priority — same three levels (table cells use this; detail uses the
// full one, which is now identical).
export const TicketPriorityShort = {
	p1: "High",
	p2: "Medium",
	p3: "Low",
	p4: "Low",
} as const;

// The three priority levels offered in the picker (create + edit) + the filter,
// highest-first. Deliberately a SUBSET of the enum — p4 renders "Low" but is
// never selectable (Marcie R6 "ditch p4"). SINGLE source so the create form, the
// properties modal, and the list filter can't drift.
export const PICKER_TICKET_PRIORITIES = ['p1', 'p2', 'p3'] as const;
export const TICKET_PRIORITY_OPTIONS: ReadonlyArray<{ value: string; label: string }> =
	PICKER_TICKET_PRIORITIES.map((value) => ({ value, label: TicketPriority[value] }));

// Stage 34a — TicketTrackingStatus mirrors the existing
// TrackingStatus const but lives under its own category name so
// the cos-status-badge category prop semantics stay explicit
// ('TicketTrackingStatus' for ticket-row use). Both consts are
// kept (TrackingStatus is referenced from the work-priority
// display + module-status surfaces that pre-date Stage 34a).
export const TrackingStatus = {
	on_track: "On Track",
	at_risk: "At Risk",
	blocked: "Blocked",
	deferred: "Deferred",
} as const;

export const TicketTrackingStatus = {
	on_track: "On Track",
	at_risk: "At Risk",
	blocked: "Blocked",
	deferred: "Deferred",
} as const;

export const TicketImpactRating = {
	performance_usability: "Performance & Usability",
	quality_of_life: "Quality of Life",
	critical: "Critical",
} as const;

export const TicketCurrentTeam = {
	support: "Support",
	engineering: "Engineering",
	product: "Product",
	implementation: "Implementation",
	cs: "Customer Success",
} as const;

// Stage 34b — rework taxonomy. Spec §3.4 row return_reasons is
// enum[] but the spec was silent on the values; these seven are
// our extension grounded in real-world engineering rework.
// Update ValidateReturnReason in backend/internal/model/
// validators.go in lockstep if this list changes.
export const TicketReturnReason = {
	missing_requirements: "Missing requirements",
	regression_introduced: "Regression introduced",
	incomplete_testing: "Incomplete testing",
	scope_unclear: "Scope unclear",
	qa_failed: "QA failed",
	blocked_by_dependency: "Blocked by dependency",
	needs_more_context: "Needs more context",
} as const;

export const TicketSource = {
	portal: "Portal",
	// Marcie R9 — "email" was missing entirely despite being the second most
	// common value (14,612 tickets); it was only rendering via the titleCase
	// fallback. api/phone/other come from the Freshdesk import, and she named
	// what they mean: "API should change to Control Panel - what it's noting
	// there is the fact that they used a widget in the control panel to create
	// the ticket", and phone "is the default when someone from our support team
	// adds a ticket on behalf of someone".
	email: "Email",
	api: "Control Panel",
	phone: "Phone",
	// Marcie R10 — "just go with email" for the 55 imported `other` tickets.
	// The stored enum value stays `other`; only the display label changes.
	other: "Email",
	chat: "Chat",
	internal: "Internal",
	slack: "Slack",
	migrated: "Migrated",
} as const;

// The Source values offered in the edit dropdown. Distinct labels only — `other`
// is deliberately EXCLUDED (Marcie R10 mapped it to "Email", so listing it would
// show "Email" twice, the same duplicate-label trap as p3/p4 = "Low"). The read
// path keeps `other: "Email"` in the map above so existing rows still render;
// the picker normalizes `other` -> `email` for editing (a free cleanup on save).
export const TICKET_SOURCE_OPTIONS: ReadonlyArray<{ value: string; label: string }> =
	(Object.entries(TicketSource) as Array<[string, string]>)
		.filter(([v]) => v !== 'other')
		.map(([value, label]) => ({ value, label }));

// Normalize a stored source into the value the dropdown should show selected.
// `other` collapses onto `email` (they display identically); everything else is
// itself.
export function normalizeTicketSource(v: string): string {
	return v === 'other' ? 'email' : v;
}

export const TicketVisibility = {
	internal_only: "Internal Only",
	partner_visible: "Partner Visible",
} as const;

export const PartnerStatus = {
	prospect: "Prospect",
	lead: "Lead",
	active_partner: "Active Partner",
	// Partner round 1 (Marcie) — lifecycle 'other' = a former partner. This
	// is a display-only pseudo-status (the stored `status` is unchanged); the
	// badge derives it via utils/partnerStatus.effectivePartnerStatus.
	former_partner: "Former Partner",
	// A partner_statuses 'other' record: an organisation that is not a library
	// we serve. Display-only key (see utils/partnerStatus.effectivePartnerStatus);
	// a bare `other` key would collide with the ticket source label.
	partner_other: "Other",
	churned: "Churned",
	suspended: "Suspended",
} as const;

export const PartnerTier = {
	paperback: "Paperback",
	hardcover: "Hardcover",
	first_edition: "First Edition",
} as const;

export const HappinessScore = {
	at_risk: "At Risk",
	doing_okay: "Doing Okay",
	happy: "Happy",
} as const;

export const PurchaseType = {
	modular: "Modular",
	package: "Package",
} as const;

// Stage 36b — spec §3.2 v5.2 contact role taxonomy. Replaces the
// legacy 8-token "function" enum (primary/billing/implementation/
// technical/support/executive/it/chat) with the 9-token
// "organizational title" set per spec. Stage 36a's migration 000030
// remapped production data; the displayLabel category here ships
// the human-readable forms for those tokens.
export const ContactRole = {
	primary_implementation_contact: "Primary Implementation Contact",
	implementation_contact: "Implementation Contact",
	executive_director: "Executive Director",
	assistant_director: "Assistant Director",
	marketing_manager: "Marketing Manager",
	accounting_contact: "Accounting Contact",
	it_director: "IT Director",
	it_team: "IT Team",
	executive_team: "Executive Team",
} as const;

export const ProjectStatus = {
	planning: "Planning",
	active: "Active",
	on_hold: "On Hold",
	completed: "Completed",
	cancelled: "Cancelled",
} as const;

// Stage 42b — extended project taxonomy. Legacy `internal` /
// `external` stay registered for back-compat with rows pre-42b.
export const ProjectType = {
	customer_onboarding: "Partner onboarding",
	custom_dev: "Custom dev",
	support_escalation: "Support escalation",
	internal_product: "Internal product",
	internal_ops: "Internal ops",
	// 28 October 2026: an engineering epic is a project of this type, with no library.
	engineering_epic: "Engineering epic",
	internal: "Internal",       // legacy
	external: "External",       // legacy
} as const;

// Stage 42b — three-tone health flag for the implementation-team
// dashboard. Friendly labels chosen to read well in dropdowns
// + on the row indicator dot.
export const ProjectHealth = {
	green: "Healthy",
	yellow: "At risk",
	red: "Off track",
} as const;

export const ModuleStatus = {
	not_purchased: "Not Purchased",
	purchased: "Purchased",
	in_progress: "In Progress",
	live: "Live",
	suspended: "Suspended",
	// Module-specific (Create / Challenge enums):
	full_website: "Full Website",
	widgets_only: "Widgets Only",
	using: "Using",
	not_using: "Not Using",
} as const;

// Stage 40 — single source of truth for the 11 canonical Communico
// modules. Mirrors `backend/internal/model/module.go` ModuleName +
// AllModuleNames(). Frontend surfaces that previously hard-coded
// the module list (cos-kb-list, cos-kb-editor, anywhere doing
// per-module dropdowns or tiles) should import from here.
//
// The KB UI uses this to build the category-tiles row on /kb;
// `general` is added inline by the consuming component when an
// "unscoped" tile is wanted, because it isn't a real module.
export interface ModuleDef {
	key: string;
	label: string;
	// Stage 41a.1 — per-module accent color (hex). Used by the
	// KB article view's top accent line + category chip + active
	// TOC item, and available to any future surface that wants
	// to tint by module. All values pass WCAG AA on white at
	// text sizes (≥ 4.5:1 contrast).
	color: string;
}

export const MODULE_REGISTRY: ReadonlyArray<ModuleDef> = [
	{ key: "core",      label: "Core",      color: "var(--cos-brand-700)" }, // teal-dark (brand anchor)
	{ key: "create",    label: "Create",    color: "#7C3AED" }, // violet
	{ key: "attend",    label: "Attend",    color: "var(--cos-danger-500)" }, // red
	{ key: "broadcast", label: "Broadcast", color: "var(--cos-warning-500)" }, // orange
	{ key: "challenge", label: "Challenge", color: "var(--cos-success-500)" }, // green
	{ key: "connect",   label: "Connect",   color: "var(--cos-info-500)" }, // blue
	{ key: "explore",   label: "Explore",   color: "#DB2777" }, // pink
	{ key: "interact",  label: "Interact",  color: "var(--cos-brand-500)" }, // cyan
	{ key: "reserve",   label: "Reserve",   color: "var(--cos-warning-500)" }, // amber
	{ key: "roam",      label: "Roam",      color: "#4F46E5" }, // indigo
	{ key: "schedule",  label: "Schedule",  color: "var(--cos-success-500)" }, // emerald
] as const;

// moduleColor — quick lookup helper for surfaces that don't
// want to walk the registry themselves. Defaults to the brand
// teal-dark for unknown categories (e.g. "general",
// "implementation" — non-module operational buckets).
export function moduleColor(key: string): string {
	const m = MODULE_REGISTRY.find(m => m.key === key);
	return m ? m.color : "var(--cos-brand-700)";
}

// ModuleLabels — quick keyed map for displayLabel('Module', key)
// lookups. Built from MODULE_REGISTRY so the two stay in lockstep.
export const Module = MODULE_REGISTRY.reduce<Record<string, string>>(
	(acc, m) => { acc[m.key] = m.label; return acc; },
	{},
) as Record<string, string>;

export const ImplTaskStatus = {
	not_started: "Not Started",
	in_progress: "In Progress",
	blocked: "Blocked",
	completed: "Completed",
} as const;

export const KBStatus = {
	draft: "Draft",
	in_review: "In Review",
	published: "Published",
	archived: "Archived",
} as const;

export const KBVisibility = {
	internal_only: "Internal Only",
	partner_visible: "Partner Visible",
} as const;

// Stage 36b — Partner data hygiene (spec §3.1 + §3.2).
// RoomBooker is the spec §3.1 Communico-native room-booking
// integration enum; AnnualReviewStatus is the spec §3.1 Annual
// Reviews subsection's status enum.
export const RoomBooker = {
	using: "Using",
	not_using: "Not Using",
} as const;

export const AnnualReviewStatus = {
	attempted_1: "Attempted 1",
	attempted_2: "Attempted 2",
	attempted_3: "Attempted 3",
	complete: "Complete",
} as const;

// Annual Report module (docs/specs/annual-report/spec.md §3 + the
// 00-COS-RETARGET.md addendum). Distinct from, and unrelated to,
// AnnualReviewStatus above, which is the partner-CRM annual review
// feature (Stage 36a, handler/annual_review.go); the retarget addendum
// calls out that name collision explicitly so it isn't repeated here.
export const AnnualReportStatus = {
	draft: "Draft",
	in_review: "In Review",
	certified: "Certified",
	exported: "Exported",
} as const;

// Stage 35b — Deals (spec §3.3 + §10A.1 + §10.3). Mirrors the
// backend per-pipeline whitelist in model/validators.go
// dealPipelineStages. Update this map AND the backend in lockstep
// (punt-list S3 covers folding both into a configurable workflow
// table). DealStage is the union across both pipelines; the
// detail-view stage <select> narrows further to the current deal's
// pipeline.
export const DealPipeline = {
	sales: "Sales",
	renewals: "Renewals",
} as const;

export const DealStage = {
	// Sales pipeline (spec §10A.1)
	contact_made: "Contact Made",
	appointment_scheduled: "Appointment Scheduled",
	demo_complete: "Demo Complete",
	quote_sent: "Quote Sent",
	final_demo: "Final Demo",
	contract_sent: "Contract Sent",
	won: "Won",
	lost: "Lost",
	// Renewals pipeline (spec §10.3)
	approaching_expiration: "Approaching Expiration",
	renewal_sent: "Renewal Sent",
	renewed: "Renewed",
	churned: "Churned",
} as const;

export const DealType = {
	new_business: "New Business",
	existing_business: "Existing Business",
} as const;

// Stage 20 — admin /users surface. Mirrored from backend
// model.User.Role's comment + validators.go ValidateUserRole.
// Stage 23-followup — added `qa` (QA Engineer) when seeding more
// specialist staff to widen the AI triage candidate pool.
export const UserRole = {
	executive: "Executive",
	engineering: "Engineering",
	support: "Support",
	implementation: "Implementation",
	cs: "Customer Success",
	sales: "Sales",
	qa: "QA Engineer",
	admin: "Admin",
	// Stage 49 — granted only via the audited grant-super-admin CLI.
	super_admin: "Super Admin",
	partner_admin: "Partner Admin",
	partner_staff: "Partner Staff",
} as const;

export const ILSType = {
	sierra: "Sierra",
	polaris: "Polaris",
	koha: "Koha",
	evergreen: "Evergreen",
	sirsi_symphony: "Sirsi Symphony",
	sirsi_horizon: "Sirsi Horizon",
	wise: "Wise",
} as const;

// Preserve (digital archives) enums — Stage 1.
export const PreserveItemType = {
	photograph: "Photograph",
	document: "Document",
	audio: "Audio",
	video: "Video",
	newspaper: "Newspaper",
	map: "Map",
	object: "Object",
	manuscript: "Manuscript",
} as const;

export const PreserveItemStatus = {
	draft: "Draft",
	in_review: "In Review",
	published: "Published",
	withdrawn: "Withdrawn",
} as const;

export const PreserveCollectionStatus = {
	draft: "Draft",
	published: "Published",
	archived: "Archived",
} as const;

export const PreserveAccessLevel = {
	public: "Public",
	staff_only: "Staff Only",
} as const;

export const PreserveCopyrightStatus = {
	public_domain: "Public Domain",
	in_copyright: "In Copyright",
	unknown: "Unknown",
	creative_commons: "Creative Commons",
	contact_for_permission: "Contact for Permission",
} as const;

// Community engagement (P2) — the moderation worklist.
export const PreserveContributionKind = {
	upload: "Upload",
	recollection: "Recollection",
	suggested_edit: "Suggested edit",
	tag_suggestion: "Tag suggestion",
} as const;

export const PreserveContributionStatus = {
	pending: "Pending",
	approved: "Approved",
	rejected: "Rejected",
	declined: "Declined",
} as const;

// Plain-language copy for the patron's copyright choice on an upload —
// distinct vocabulary from PreserveCopyrightStatus (which describes an
// ITEM's rights state, not a contributor's grant).
export const PreserveContributionCopyrightChoice = {
	transfer: "Full transfer to the library",
	cc_nc_nd: "CC BY-NC-ND: contributor keeps commercial rights",
} as const;

// Roster (staff rostering) enums — Stage 1.
export const RosterWorkerKind = {
	staff: "Staff",
	casual: "Casual",
	volunteer: "Volunteer",
	// CLC row 42 — a placeholder for a position not yet filled.
	vacancy: "Vacancy (open position)",
} as const;

export const RosterEmploymentBasis = {
	full_time: "Full Time",
	part_time: "Part Time",
	casual: "Casual",
	volunteer: "Volunteer",
	// CLC row 42 — engaged through their own business, not an employee.
	contractor: "Contractor",
} as const;

// CLC row 02 — a skill grant's proficiency, lowest to highest.
export const RosterSkillProficiency = {
	basic: "Basic",
	working: "Working",
	expert: "Expert",
} as const;

// CLC row 55 — a shift's explicit work status/location.
export const RosterWorkMode = {
	on_site: "On-site",
	remote: "Remote",
	off_site: "Off-site",
} as const;

export const RosterShiftStatus = {
	draft: "Draft",
	published: "Published",
	cancelled: "Cancelled",
	worked: "Worked",
} as const;

export const RosterClaimStatus = {
	pending: "Pending",
	approved: "Approved",
	declined: "Declined",
	withdrawn: "Withdrawn",
} as const;

export const RosterLeaveStatus = {
	pending: "Pending",
	approved: "Approved",
	declined: "Declined",
	cancelled: "Cancelled",
} as const;

export const RosterVolunteerApplicationStatus = {
	pending: "Pending",
	approved: "Approved",
	declined: "Declined",
} as const;

export const RosterSwapStatus = {
	pending: "Awaiting colleague",
	accepted: "Awaiting approval",
	approved: "Approved",
	declined: "Declined",
	cancelled: "Cancelled",
} as const;

// CLC row 56 — an open shift's own offer (whole shift or a slice), not
// to be confused with RosterSwapStatus (a peer-to-peer trade's states).
export const RosterShiftOfferStatus = {
	open: "Open",
	held: "Held",
	claimed: "Claimed",
	expired: "Expired",
	cancelled: "Cancelled",
} as const;

// CLC row 51 — one worker's turn in a sequential ask-and-wait chain.
export const RosterSubPoolAskStepStatus = {
	pending: "Waiting",
	declined: "Declined",
	lapsed: "No response",
	accepted: "Accepted",
} as const;

export const RosterAvailabilityKind = {
	available: "Available",
	unavailable: "Unavailable",
	preferred: "Preferred",
} as const;

// Roster breaks phase 2b (roster-breaks-spec.md) — a placed interval on
// a shift. `planned`/`approved` are the two settled states (the ones
// that count toward coverage); `requested` is a worker's own proposal
// awaiting a decision (phase 3) and must read as pending, not settled.
export const RosterBreakKind = {
	meal: "Meal",
	rest: "Rest",
} as const;

export const RosterBreakStatus = {
	planned: "Planned",
	requested: "Requested",
	approved: "Approved",
	declined: "Declined",
} as const;

// Stage 9 — time clock (roster-timeclock-brief.md).
export const RosterTimeEntryStatus = {
	pending: "Pending review",
	approved: "Approved",
	rejected: "Rejected",
} as const;

export const RosterTimeEntryChannel = {
	nfc: "NFC tap",
	qr: "QR scan",
	manual: "Manual",
} as const;

export const RosterServicePointCategory = {
	desk: "Desk",
	roving: "Roving",
	programs: "Programs",
	back_office: "Back Office",
	other: "Other",
} as const;

// Roster commitments spine (Plan's commitments rail — roster-rethink §2).
export const RosterCommitmentKind = {
	program: "Program",
	meeting: "Meeting",
	training: "Training",
	appointment: "Appointment",
	outreach: "Outreach",
	other: "Other",
} as const;

export const RosterCommitmentState = {
	tentative: "Tentative",
	confirmed: "Confirmed",
	changed_after_incorporation: "Changed since locked in",
	cancelled: "Cancelled",
	// FR-62 — a worker's own submitted request, before a manager decides it.
	requested: "Requested",
	declined: "Declined",
} as const;

export const RosterCommitmentVisibility = {
	public: "Public",
	internal: "Internal",
	confidential: "Confidential",
} as const;

// FR-53, the structured cause behind a reschedule or a cancellation,
// shared by both.
export const RosterCommitmentChangeCause = {
	get patron_request() { return `${activeCardholderTerm().Singular} request`; },
	staff_unavailable: "Staff unavailable",
	library_closure: "Library closure",
	scheduling_conflict: "Scheduling conflict",
	other: "Other",
} as const;

// What ComputeCompleteness (backend) can report missing (FR-62).
export const RosterCommitmentMissingField = {
	owner: "owner",
	location: "location",
	staffing: "staffing",
	qualification: "qualification",
} as const;

// FR-52 — the field names a re-import disagreement can name.
export const RosterCommitmentImportField = {
	title: "Public title",
	starts_at: "Start time",
	ends_at: "End time",
	visibility: "Visibility",
	branch_id: "Location",
} as const;

// Roster W4b — the Staff tab's today-status chip.
export const RosterWorkerTodayStatus = {
	active: "Active",
	on_leave: "On leave",
} as const;

// Roster W4b — the Contact & HR tab's preferred-contact-method field.
export const RosterPreferredContact = {
	"": "Not set",
	mobile_sms: "Mobile / SMS",
	email: "Email",
	phone: "Phone",
} as const;

// fleet/status-pill-tokens (2026-08-20) — docs/roster-design-system-
// audit.md's blocked-on-the-primitive vocabularies. RosterLeaveStatus
// above already covers the fifth (pending/approved/declined/cancelled).

// Compliance flag severity (cos-roster-week.ts .flag-badge/.sev-chip).
export const RosterFlagSeverity = {
	info: "Info",
	warning: "Warning",
	violation: "Violation",
} as const;

// Coverage-score band (cos-roster-page.ts .chip-ok/.chip-good/.chip-warn/
// .chip-alert; cos-roster-timeline.ts _scoreClass). The chip today shows
// the raw score, not this label — this is for a future migration onto
// cos-status-pill, whose labels mirror the audit's own band words
// ("90+ full, 70-89 good, 40-69 thin, <40 critical").
export const RosterCoverageBand = {
	ok: "Full",
	good: "Good",
	warn: "Thin",
	alert: "Critical",
} as const;

// Coverage-interval rule verdict, the WCAG table fallback
// (cos-roster-timeline.ts STATUS_LABEL / .status-chip). Labels mirror
// that local map verbatim so a migration is label-identical.
export const RosterCoverageIntervalStatus = {
	ok: "OK",
	under: "Under minimum",
	no_skill: "Missing required skill",
	// rows 10/12 — a rule's minimum held, only its preferred target
	// didn't. Never "under": that word is reserved for an invalid hour.
	below_target: "Below target",
} as const;

// Row 45 — RosterCoverageDay/RosterCoverageNow.operating_level: the
// highest of a library's own declared staffing-priority levels whose
// per-point minimums held for the day, or the two ends of that scale.
export const RosterOperatingLevel = {
	fully_staffed: "Fully staffed",
	preferred: "Preferred",
	minimum_viable: "Minimum viable",
	emergency: "Emergency",
	below_emergency: "Below emergency",
} as const;

// CLC row 67 — exception-aware generation: what kind of known change
// generation applied before writing a shift, and what it did about it.
export const RosterGenerationExceptionKind = {
	leave: "Approved leave",
	trade: "Approved trade",
	transfer: "Branch transfer",
	eligibility: "Skill eligibility",
} as const;

export const RosterGenerationExceptionOutcome = {
	opened: "Opened the slot",
	kept: "Kept as reassigned",
	skipped: "Skipped",
} as const;

export const RosterGenerationSourceKind = {
	template: "Week template",
	rotation: "Rotation",
	desk_copy: "Desk copy",
	copy_week: "Week copy",
} as const;

// Presenter booking status (cos-roster-booking-panel.ts
// BOOKING_STATUS_LABEL). Labels mirror that local map verbatim.
export const RosterBookingStatus = {
	inquiry: "Inquiry",
	confirmed: "Confirmed",
	invoice_sent: "Invoice sent",
	paid: "Paid",
	cancelled: "Cancelled",
} as const;

// Credential expiry report (fleet/roster-credential-expiry) — one
// worker/credential grant's status relative to the report's window.
// Keys are prefixed (credential_*) so they stay out of the flat
// badge-tokens.ts tone map's collision surface with the unrelated
// report-job `expired` below (that one is a muted terminal grey; an
// expired CREDENTIAL is an active compliance problem and reads red).
export const RosterCredentialExpiryStatus = {
	credential_expired: "Expired",
	credential_expiring: "Expiring soon",
	credential_valid: "Valid",
} as const;

// Reports (data-extract) builder — async job status.
export const ReportJobStatus = {
	queued: "Queued",
	running: "Running",
	ready: "Ready",
	failed: "Failed",
	cancelled: "Cancelled",
	expired: "Expired",
} as const;

// Converse Stage 2 — a Nurture campaign's send lifecycle
// (draft -> sending -> sent; a failed run returns to draft).
export const NurtureCampaignStatus = {
	draft: "Draft",
	sending: "Sending",
	sent: "Sent",
	archived: "Archived",
} as const;

// Quick email (docs/stage-briefs/quick-email-brief.md) — PURELY
// DESCRIPTIVE (model.CampaignKind*): a quick email rides the identical
// audience-resolve + Hub-dispatch + one-shot-send machinery as a
// standard campaign. `standard` never renders a badge in the campaigns
// list — only `quick` does.
export const NurtureCampaignKind = {
	standard: "Standard",
	quick: "Quick",
} as const;

// Automated Journeys (§10.5) — draft<->active<->paused; archived is
// terminal (exits everyone, no reactivation).
export const NurtureJourneyStatus = {
	draft: "Draft",
	active: "Active",
	paused: "Paused",
	archived: "Archived",
} as const;

// Surveys (§13, v1 spine) — draft -> active -> archived; no "paused"
// (a survey has no long-running graph to pause).
export const NurtureSurveyStatus = {
	draft: "Draft",
	active: "Active",
	archived: "Archived",
} as const;

// A circulate hold's status, as the live ILS reports it on the customer
// record. The vocabulary is circulate's own request status (the same
// values ils-holds-panel.ts maps) — a compound of the request's open/
// closed state and where the item currently is, which is why the raw
// value reads as two words joined by an underscore and never belongs on
// screen.
export const NurtureHoldStatus = {
	open_awaiting_pickup: "Ready for pickup",
	open_in_transit: "In transit",
	open_pending: "Waiting",
	closed_expired: "Expired",
	closed_cancelled: "Cancelled",
	closed_fulfilled: "Collected",
} as const;

// §9.6 the Boardroom — the meeting status state machine (forward-only:
// scheduled -> notice_posted -> held -> published; the one exception,
// un-publish, moves back to held).
export const NurtureBoardMeetingStatus = {
	scheduled: "Scheduled",
	notice_posted: "Notice Posted",
	held: "Held",
	published: "Published",
} as const;

// §9.6 the Boardroom — the three lead-time notices fired on the
// scheduler tick and journalled in nurture_board_notice_log.
export const NurtureBoardNoticeKind = {
	open_meeting: "Open-meetings notice",
	packet_ready: "Packet ready",
	posting_deadline: "Posting deadline reminder",
} as const;

// Communications Hub producer keys (comms_messages.producer) — the wire
// carries the module's internal name; the staff console renders the word a
// librarian uses (2026-08-19 demo-prep pass). Unknown producers fall back
// to titleCase, so a new module needs no entry to render acceptably.
export const CommsProducer = {
	circulation: "Circulation",
	attend: "Events",
	reserve: "Rooms",
	roster: "Staff scheduling",
	nurture: "Marketing",
	tickets: "Support",
} as const;

// Comms Hub delivery statuses (comms_message_deliveries.status).
export const CommsDeliveryStatus = {
	queued: "Queued",
	sending: "Sending",
	sent: "Sent",
	delivered: "Delivered",
	bounced: "Bounced",
	complained: "Complained",
	suppressed: "Suppressed",
	failed: "Failed",
	needs_review: "Needs review",
} as const;

// The events2_drafts word vocabulary Reserve's list endpoint returns on
// event-held bookings (reserve_bookings_list.go reserveEventStatusWords).
// The backend deliberately sends the machine words; render through here.
export const ReserveEventStatus = {
	draft: "Draft",
	changes_requested: "Changes requested",
	awaiting_review: "Awaiting review",
	published: "Published",
	unpublished: "Unpublished",
} as const;

// Circulate account-links relationship vocabulary (parent/child, school
// account/pupil, organization/staff). Scoped to the linked-accounts panel
// on the Circulate patron record — a Circulate-owned enum, not a COS
// domain entity, but still rendered through the shared registry per the
// no-raw-enum rule rather than a local map.
export const AccountLinkRelationship = {
	family: "Family",
	school: "School",
	organization: "Organization",
	other: "Other",
} as const;

// Brief BE (Paul, 2026-09-07): billing's contract vocabulary. Three
// facts wear three different words and none of them is "cancelled".
// "Won't renew" is the customer saying they will not renew, and nothing
// about invoicing or revenue moves. "Ended early" is a mid-term
// termination that reverses lines and credits money. A lapse is just a
// term running out. The stored enum keeps `cancelled` for the mid-term
// termination, so this map is the only place that word is translated
// out of, and the DERIVED values (lapsed, pending, no_term) are here
// too because every contract badge in billing shows the derived
// status, not the stored one.
export const ContractStatus = {
	draft: "Draft",
	active: "Active",
	lapsed: "Lapsed",
	pending: "Not started",
	no_term: "No term",
	expired: "Expired",
	cancelled: "Ended early",
	superseded: "Superseded",
	closed_out: "Closed out",
} as const;

// Cheque intake (migration 000462). Two vocabularies, both closed.
//
// A BATCH is one trip to the bank, so its statuses are named for where
// the cheques physically are: 'deposited' reads "At the bank" rather
// than "Deposited", because the word somebody wants on screen is the
// answer to "have these gone in yet".
export const DepositBatchStatus = {
	draft: "Draft",
	recorded: "Recorded",
	deposited: "At the bank",
	matched: "Matched",
} as const;

// An ITEM is one cheque. 'exception' reads "Needs a decision": it is
// not an error, it is a cheque the matcher would not place on its own,
// and the word has to say that a person is what it is waiting for.
export const DepositItemStatus = {
	proposed: "Proposed",
	confirmed: "Confirmed",
	recorded: "Recorded",
	// 'partial' is a consortium cheque half in the ledger: some member
	// libraries' payments posted and the rest did not. "Partly
	// recorded" rather than "Failed", because the money that went in is
	// real and the rest is still a cheque somebody can finish.
	partial: "Partly recorded",
	exception: "Needs a decision",
} as const;

// Bank activity (migration 000472). Two closed vocabularies.
//
// A MATCH STATUS answers "does COS know what this money is". 'unmatched'
// reads "Not explained" rather than "Unmatched": the reader's question
// is about the money, not about a database column, and the word has to
// say that the line is waiting on somebody.
export const BankMatchStatus = {
	unmatched: "Not explained",
	matched_payment: "Matched to a payment",
	matched_batch: "Matched to a cheque batch",
	ignored: "Ignored",
	// Migration 000478. "Set aside by a rule" rather than "Not
	// receivable": the reader's question is what happened to the money,
	// and the answer has to say that a machine decided it and which one.
	// It is a different word from "Ignored" on purpose, because that one
	// means a person decided.
	not_receivable: "Set aside by a rule",
} as const;

// A TRANSACTION TYPE is QuickBooks' own entity name, stored verbatim
// because it is half the unique key. "SalesReceipt" is a QuickBooks
// spelling and not English, and 'Purchase' is money going OUT, which is
// the opposite of what the word suggests to a reader looking at a bank
// account, so both are named here rather than title-cased.
export const BankTxnType = {
	Deposit: "Deposit",
	Payment: "Customer payment",
	SalesReceipt: "Sales receipt",
	Transfer: "Transfer",
	JournalEntry: "Journal entry",
	Purchase: "Payment out",
} as const;

// A bank line rule is on or off, and "off" is the undo rather than a
// disabled state: switching a rule off gives back every line it set
// aside (migration 000478). The words say that the rule is or is not
// acting, not that it is healthy or broken.
export const BankRuleStatus = {
	active: "On",
	inactive: "Off",
} as const;

// Explore, format_bucket: the TOP-LEVEL grouping axis, and the one that is
// part of the work key. DVDs and Blu-rays land in one work, books and
// audiobooks and eBooks in another, and this names which of those a result
// is (Chris, 2026-09-08). Exactly five values, COS-wide, so unlike
// format_groups (about ten PER CLIENT, the library's own vocabulary) this
// one is a closed set that belongs in a label map.
export const ExploreFormatBucket = {
	book: "Book",
	video: "Video",
	music: "Music",
	periodical: "Periodical",
	other: "Other",
} as const;

// Explore admin, Imports (fleet/cos-explore-imports): explore_import_file.state.
export const ExploreImportFileState = {
	present: "Present",
	processing: "Processing",
	processed: "Processed",
	missing: "Missing",
	failed: "Failed",
	ignored: "Ignored",
} as const;

// Thrive Collections, collection_code (thrive_balance's own physical
// format vocabulary, a different axis from the Library of Things
// `lend:`-namespaced codes, which libraryOfThingsLabel in
// thrive/collections.ts handles separately). Every distinct code Seasons
// carries, confirmed live against thrive_balance (17 September 2026): a
// code with no entry here still renders through displayLabel's
// titleCase fallback rather than a blank cell, but cd/dvd/ill/eres would
// title-case wrong ("Cd", "Dvd", "Ill"), so they get explicit entries.
export const ThriveCollectionCode = {
	audiobook: "Audiobook",
	board_book: "Board book",
	book: "Book",
	cd: "CD",
	dvd: "DVD",
	equipment: "Equipment",
	eres: "eResource",
	hotspot: "Hotspot",
	ill: "Interlibrary loan",
	kit: "Kit",
	large_print: "Large print",
	magazine: "Magazine",
	museum_pass: "Museum pass",
	reference: "Reference",
	tech_kit: "Tech kit",
	video_game: "Video game",
} as const;

// Training (fleet/training-ui): the admin board's per-person status
// filter (docs/training-spec.md 4.5).
export const TrainingBoardStatus = {
	not_started: "Not started",
	in_progress: "In progress",
	failed: "Failed",
	passed: "Passed",
} as const;

// Preserve tag types (preserve_tags.tag_type, and the vocabulary and
// proposed-tag rows built on them): the closed set the backend validates.
export const PreserveTagType = {
	subject: "Subject",
	person: "Person",
	place: "Place",
	time_period: "Time period",
	material: "Material",
	genre: "Genre",
} as const;

// preserve_tag_candidates.status: what a librarian has decided about a
// term the tally found in a recording.
export const PreserveCandidateStatus = {
	pending: "To review",
	accepted: "Accepted",
	rejected: "Rejected",
} as const;

// Offline Desk (docs/stage-briefs/desk-offline-contract.md): the kind on an offline follow-up
// row, from the exception kinds Circulate reports on replay plus the two pull kinds.
export const DeskOfflineFollowupKind = {
	card_blocked: "Card was blocked",
	card_expired: "Card had expired",
	over_limit: "Card was over its item limit",
	get owes_over_limit() { return `${activeCardholderTerm().Singular} owed too much`; },
	was_on_loan_to_another_card: "Item was out to another card",
	already_on_loan_to_this_card: "Item was already out to this card",
	item_was_lost: "Item was marked lost",
	item_not_found: "Item not found",
	card_not_found: "Card not found",
	card_number_in_use: "New card number was already in use",
	get registration_invalid() { return `New ${activeCardholderTerm().singular} could not be created`; },
	registrations_deleted: "New cards were deleted from a desk PC",
	payment_method_not_allowed_offline: "Payment type not allowed offline",
	get overpaid() { return `${activeCardholderTerm().Singular} paid more than they owed`; },
	payment_invalid: "Payment amount was not valid",
	due_date_differs: "Due date changed",
	no_open_loan: "Item was not checked out",
	hold_shelf: "Put on the hold shelf",
	transit: "Send to another branch",
} as const;

export const DeskOfflineDeviceStatus = {
	active: "Active",
	revoked: "Revoked",
} as const;

// explore_import_run.state, plus one value the backend never sends.
// `stalled` is display-only: types/explore.ts exploreRunIsStalled treats a
// `running` row with no heartbeat for 15+ minutes as stalled, because
// nothing marks a run failed when the process itself dies. The imports
// screen substitutes this label for such a row rather than showing the
// stored `running` value, which would read as alive.
export const ExploreImportRunState = {
	queued: "Queued",
	running: "Running",
	stalled: "Stalled",
	succeeded: "Succeeded",
	failed: "Failed",
	cancelled: "Cancelled",
} as const;

// Category map — links the string identifier passed to displayLabel()
// to the actual enum object. Keeping the registry explicit (vs. a big
// `Record<string, Record<string, string>>` cast) means TypeScript
// flags typos at the call site.
// Agent platform admin (/admin/agents, PRD 11). Backend enums from
// internal/agentplatform; the admin page renders every one through here.
export const AgentStatus = {
	draft: "Draft",
	active: "Active",
	paused: "Paused",
	retired: "Retired",
} as const;

export const AgentVersionKind = {
	create: "Creation",
	save: "Save",
	activate: "Activation",
} as const;

export const AgentClaudeSignin = {
	unknown: "Not reported",
	needed: "Sign-in needed",
	pending: "Signing in",
	ok: "Signed in",
	expired: "Expired",
} as const;

export const AgentSlackInstall = {
	none: "No app",
	created: "App created",
	installed: "Installed",
} as const;

export const AgentSlackStatus = {
	connected: "Connected",
	reconnecting: "Reconnecting",
	failed: "Failed",
} as const;

export const AgentAutonomy = {
	never: "Never",
	propose: "Propose for approval",
	act_then_tell: "Act, then tell",
	act_alone: "Act alone",
} as const;

export const AgentHandlingMode = {
	humans_handle: "Humans handle",
	shared: "Shared",
	agent_handles: "Agent handles",
} as const;

export const AgentMailRole = {
	lead: "Lead",
	notes: "Notes only",
} as const;

export const AgentChannelPurpose = {
	home: "Home",
	alerts: "Alerts",
	digest: "Digest",
	handoff: "Handoff",
} as const;

export const AgentToolKind = {
	read: "Read",
	propose: "Propose",
	execute: "Execute",
	draft: "Draft",
} as const;

export const AgentHostKind = {
	mac: "Mac mini",
	ecs: "ECS",
	coder: "Coder workspace",
} as const;

export const AgentAction = {
	"email.reply": "Reply to email",
	"email.send": "Send new email",
	"slack.post": "Post in Slack",
	"dbops.change": "Change customer data",
	"purchase.order": "Place a purchase order",
} as const;

export const AgentScopeKey = {
	address: "Address",
	recipient_domains: "Recipient domains",
	channel: "Channel",
	risk_tier: "Risk tier",
	condition: "Condition",
} as const;

export const AgentReadinessStep = {
	host: "Host assigned",
	slack: "Slack app installed",
	claude: "Claude signed in",
	mcp: "MCP credential",
} as const;

export const AgentAlertSource = {
	aws: "AWS",
	datadog: "Datadog",
	freshdesk: "Freshdesk",
} as const;

// Repeating tasks from chat (agent missions M1): the row's status and why it
// stopped (agent_recurring_tasks.status, status_reason).
export const AgentRecurringStatus = {
	active: "Active",
	paused: "Paused",
	cancelled: "Cancelled",
} as const;

export const AgentRecurringReason = {
	failed_runs: "Its last three runs failed",
	owner_inactive: "Its owner's account is inactive",
	agent_retired: "Its agent was retired",
	ended: "It reached its end date",
	cancelled_by_owner: "Cancelled by its owner",
	cancelled_by_admin: "Cancelled by an admin",
	paused_by_owner: "Paused by its owner",
	paused_by_admin: "Paused by an admin",
} as const;

// Playbooks (agent missions M5): agent_playbooks.status, a spec input's
// kind, and where a mission input came from.
export const AgentPlaybookStatus = {
	active: "Active",
	retired: "Retired",
} as const;

export const AgentPlaybookInputKind = {
	text: "Text",
	number: "Number",
	bool: "Yes or no",
	list: "List",
	file: "Mission file",
} as const;

export const AgentMissionInputSource = {
	human: "from the person",
	derived: "derived",
	default: "playbook default",
} as const;

// Missions (agent missions M2, agent_missions.status and status_reason,
// agent_tasks.status and kind, the plan's step status, the log's kinds).
export const AgentMissionStatus = {
	planning: "Planning",
	awaiting_approval: "Awaiting approval",
	active: "Active",
	waiting_on_human: "Waiting for an answer",
	paused: "Paused",
	completed: "Completed",
	failed: "Failed",
	cancelled: "Cancelled",
	expired: "Expired",
} as const;

export const AgentMissionReason = {
	budget: "Its budget is spent",
	stalled: "It stopped making progress",
	human_silent: "Nobody answered its question for 7 days",
	owner_inactive: "Its owner is no longer active",
	lead_unavailable: "Its lead agent is paused or retired",
	stopped_in_slack: "It was stopped in Slack",
	paused_by_owner: "Paused by its owner",
	paused_by_admin: "Paused by an admin",
	cancelled_by_owner: "Cancelled by its owner",
	cancelled_by_admin: "Cancelled by an admin",
	never_planned: "The lead never started work within 2 hours",
	paused_too_long: "It was paused for 30 days",
	declined: "Its approval was declined",
	approval_expired: "Nobody decided its approval within 7 days",
	workflow_closed: "The workflow step that started it ended",
} as const;

export const AgentMissionOutcome = {
	done: "Done",
	partial: "Partly done",
	failed: "Failed",
} as const;

export const AgentTaskStatus = {
	queued: "Queued",
	delivered: "Delivered",
	accepted: "Accepted",
	running: "Running",
	waiting: "Waiting",
	done: "Done",
	failed: "Failed",
	cancelled: "Cancelled",
} as const;

export const AgentTaskKind = {
	mission: "Mission",
	handoff: "Hand-off",
	schedule: "Schedule",
	followup: "Follow-up",
	mail: "Email",
	alert: "Alert",
} as const;

export const AgentTaskOutcome = {
	done: "Done",
	needs_input: "Needs input",
	declined: "Declined",
	failed: "Failed",
	timed_out: "Timed out",
	cancelled: "Cancelled",
} as const;

export const AgentMissionStepStatus = {
	pending: "Not started",
	started: "Started",
	done: "Done",
	skipped: "Skipped",
} as const;

export const AgentMissionEventKind = {
	created: "Created",
	planned: "Planned",
	approved: "Approved",
	declined: "Declined",
	delegated: "Handed off",
	child_done: "Hand-off done",
	child_failed: "Hand-off failed",
	asked: "Asked",
	answered: "Answered",
	action: "Action",
	log: "Note",
	budget: "Budget",
	paused: "Paused",
	resumed: "Resumed",
	cancelled: "Cancelled",
	completed: "Completed",
	failed: "Failed",
	expired: "Expired",
} as const;

export const AgentMissionFileKind = {
	input: "Input",
	intermediate: "Working",
	output: "Output",
} as const;

export const AgentMissionFileSource = {
	slack: "From Slack",
	agent: "Written by an agent",
	url: "Fetched from the web",
	tool: "Made by COS",
} as const;

// Mission approval (agent missions M4): a grant line's status, the
// approval card's, and the kinds of action a line covers.
export const AgentMissionGrantStatus = {
	pending: "Waiting for approval",
	active: "Active",
	revoked: "Revoked",
	expired: "Ended",
} as const;

export const AgentMissionApprovalStatus = {
	pending: "Waiting for approval",
	executed: "Approved",
	declined: "Declined",
	expired: "Expired",
} as const;

export const AgentGrantAction = {
	"dbops.change": "Change customer data",
	"slack.post": "Post in Slack",
	"email.reply": "Reply to email",
	"email.send": "Send new email",
} as const;

export const AgentKind = {
	colleague: "Colleague",
	worker: "Worker",
} as const;

export const AgentAcceptsDelegation = {
	none: "No agent",
	all: "Any agent",
	list: "Only the agents chosen below",
} as const;

// Workflows (agent workflows W1): runs and their steps.
export const WorkflowRunStatus = {
	active: "Running",
	paused: "Paused",
	succeeded: "Finished",
	failed: "Failed",
	cancelled: "Cancelled",
} as const;

export const WorkflowRunReason = {
	budget: "Its budget is spent",
	owner_inactive: "Its owner is no longer active",
	paused_by_owner: "Paused by its owner",
	paused_by_admin: "Paused by an admin",
	no_route: "A step had nowhere to go next",
	step_failed: "A step failed",
	stalled: "It stopped making progress",
	cancelled_by_owner: "Cancelled by its owner",
	cancelled_by_admin: "Cancelled by an admin",
	ended: "It reached its end",
	max_visits: "A step ran as many times as it may",
	parent_closed: "The workflow that started it ended",
	mission_closed: "The mission that started it ended",
} as const;

export const WorkflowStepStatus = {
	ready: "Ready",
	running: "Running",
	waiting: "Waiting",
	succeeded: "Done",
	failed: "Failed",
	timed_out: "Ran out of time",
	cancelled: "Cancelled",
	skipped: "Skipped",
} as const;

export const WorkflowNodeType = {
	start: "Start",
	end: "End",
	agent: "Agent",
	tool: "Tool",
	timer: "Timer",
	decision: "Decision",
	parallel: "Parallel",
	join: "Join",
	human: "Ask people",
	mission: "Mission",
	child_workflow: "Another workflow",
	wait_event: "Wait for an event",
} as const;

// Workflow definitions (agent workflows W5).
export const WorkflowDefinitionStatus = {
	draft: "Not published yet",
	active: "Active",
	retired: "Retired",
} as const;

export const WorkflowVersionState = {
	draft: "Draft",
	published: "Live",
	superseded: "Replaced",
} as const;

export const WorkflowSubjectKind = {
	none: "Nothing in particular",
	client: "A library",
	ticket: "A ticket",
	github_pr: "A pull request",
	mission: "A mission",
} as const;

// Decision engines (agent workflows W2).
export const DecisionEngineStatus = {
	active: "Active",
	shadow_only: "Shadow only",
	off: "Off",
} as const;

export const DecisionMode = {
	live: "Live",
	shadow: "Shadow",
	fallback: "Fallback",
} as const;

export const DecisionAdapter = {
	jev: "Jev",
	openai: "OpenAI decisions",
	fake: "Test engine (no network)",
} as const;

// Code review metrics (agent workflows W5). A layer is named by its
// definition, so an unknown one title-cases.
export const ReviewSeverity = {
	important: "Important",
	nit: "Nit",
	pre_existing: "Already there",
} as const;

export const ReviewTier = {
	low: "Low risk",
	medium: "Medium risk",
	high: "High risk",
	unknown: "Risk not set",
} as const;

export const ReviewLayer = {
	security: "Security",
} as const;

export const DataClass = {
	public: "Public",
	internal_code: "Our code",
	internal_business: "Internal",
	customer: "Library data",
	get patron() { return `${activeCardholderTerm().Singular} data`; },
} as const;

const REGISTRY = {
	WorkflowRunStatus,
	WorkflowRunReason,
	WorkflowStepStatus,
	WorkflowNodeType,
	WorkflowDefinitionStatus,
	WorkflowSubjectKind,
	WorkflowVersionState,
	DecisionEngineStatus,
	DecisionMode,
	DecisionAdapter,
	DataClass,
	ReviewSeverity,
	ReviewTier,
	ReviewLayer,
	AgentStatus,
	AgentRecurringStatus,
	AgentRecurringReason,
	AgentMissionStatus,
	AgentPlaybookStatus,
	AgentPlaybookInputKind,
	AgentMissionInputSource,
	AgentMissionReason,
	AgentMissionOutcome,
	AgentTaskStatus,
	AgentTaskKind,
	AgentTaskOutcome,
	AgentMissionStepStatus,
	AgentMissionEventKind,
	AgentMissionFileKind,
	AgentMissionFileSource,
	AgentMissionGrantStatus,
	AgentMissionApprovalStatus,
	AgentGrantAction,
	AgentKind,
	AgentAcceptsDelegation,
	AgentVersionKind,
	AgentClaudeSignin,
	AgentSlackInstall,
	AgentSlackStatus,
	AgentAutonomy,
	AgentHandlingMode,
	AgentMailRole,
	AgentChannelPurpose,
	AgentToolKind,
	AgentHostKind,
	AgentAction,
	AgentScopeKey,
	AgentReadinessStep,
	AgentAlertSource,
	AccountLinkRelationship,
	CommsProducer,
	CommsDeliveryStatus,
	ReserveEventStatus,
	TicketStatus,
	TicketType,
	TicketPriority,
	TicketPriorityShort,
	TrackingStatus,
	TicketTrackingStatus,
	TicketImpactRating,
	TicketCurrentTeam,
	TicketReturnReason,
	TicketSource,
	TicketVisibility,
	PartnerStatus,
	PartnerTier,
	HappinessScore,
	PurchaseType,
	ContactRole,
	ProjectStatus,
	ProjectType,
	ProjectHealth,
	Module,
	ModuleStatus,
	ImplTaskStatus,
	KBStatus,
	KBVisibility,
	ILSType,
	UserRole,
	DealPipeline,
	DealStage,
	DealType,
	RoomBooker,
	AnnualReviewStatus,
	AnnualReportStatus,
	PreserveItemType,
	PreserveItemStatus,
	PreserveCollectionStatus,
	PreserveAccessLevel,
	PreserveCopyrightStatus,
	PreserveContributionKind,
	PreserveContributionStatus,
	PreserveContributionCopyrightChoice,
	RosterBreakKind,
	RosterBreakStatus,
	RosterWorkerKind,
	RosterEmploymentBasis,
	RosterSkillProficiency,
	RosterWorkMode,
	RosterShiftStatus,
	RosterClaimStatus,
	RosterLeaveStatus,
	RosterVolunteerApplicationStatus,
	RosterSwapStatus,
	RosterShiftOfferStatus,
	RosterSubPoolAskStepStatus,
	RosterAvailabilityKind,
	RosterTimeEntryStatus,
	RosterTimeEntryChannel,
	RosterServicePointCategory,
	RosterCommitmentKind,
	RosterCommitmentState,
	RosterCommitmentVisibility,
	RosterCommitmentChangeCause,
	RosterCommitmentMissingField,
	RosterCommitmentImportField,
	RosterWorkerTodayStatus,
	RosterPreferredContact,
	RosterFlagSeverity,
	RosterCoverageBand,
	RosterCoverageIntervalStatus,
	RosterOperatingLevel,
	RosterGenerationExceptionKind,
	RosterGenerationExceptionOutcome,
	RosterGenerationSourceKind,
	RosterBookingStatus,
	RosterCredentialExpiryStatus,
	ReportJobStatus,
	NurtureCampaignStatus,
	NurtureCampaignKind,
	NurtureJourneyStatus,
	NurtureSurveyStatus,
	NurtureHoldStatus,
	NurtureBoardMeetingStatus,
	NurtureBoardNoticeKind,
	ContractStatus,
	DepositBatchStatus,
	DepositItemStatus,
	BankMatchStatus,
	BankRuleStatus,
	BankTxnType,
	ExploreFormatBucket,
	ExploreImportFileState,
	ExploreImportRunState,
	ThriveCollectionCode,
	TrainingBoardStatus,
	PreserveTagType,
	PreserveCandidateStatus,
	DeskOfflineFollowupKind,
	DeskOfflineDeviceStatus,
} as const;

export type Category = keyof typeof REGISTRY;

/**
 * Resolve an API enum value to its display label.
 *
 * @param category The category name (one of REGISTRY's keys)
 * @param value The raw API value (snake_case)
 * @returns Mapped label, or a Title Cased fallback for unknown values
 *
 * Returns "" when value is null / undefined / empty — caller decides
 * the empty placeholder ("—" for table cells, "Unassigned" for
 * sidebar fields, etc.).
 */
export function displayLabel(category: Category, value: string | null | undefined): string {
	if (value == null || value === "") return "";
	const map = REGISTRY[category] as Record<string, string>;
	const hit = map[value];
	if (hit) return hit;
	return titleCase(value);
}

// titleCase converts `support_query` / `at_risk` to `Support Query` /
// `At Risk`. Pure: no side effects, no dependencies.
function titleCase(s: string): string {
	return s
		.split(/[_\s]+/)
		.filter(Boolean)
		.map(part => {
			if (part.length === 0) return "";
			// Preserve common acronyms.
			if (/^[A-Z]+$/.test(part)) return part;
			return part[0].toUpperCase() + part.slice(1).toLowerCase();
		})
		.join(" ");
}
